import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../axios";
import printLayoutApi from "../../api/printLayoutApi";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Select,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip
} from "@mui/material";
import {
  Save as SaveIcon,
  ContentCopy as CopyIcon,
  ViewColumn as ViewColumnIcon,
  TextFields as TextFieldIcon,
  Code as CodeIcon,
  RestartAlt as ResetIcon,
  DragIndicator as DragHandleIcon
} from "@mui/icons-material";
import { toast } from "react-toastify";

// Default columns configuration
const defaultColumns = [
  { key: "index", labelAr: "#", labelEn: "#", width: "5%", visible: true, order: 1 },
  { key: "item", labelAr: "الصنف", labelEn: "Item", width: "30%", visible: true, order: 2 },
  { key: "unit", labelAr: "الوحدة", labelEn: "Unit", width: "10%", visible: true, order: 3 },
  { key: "quantity", labelAr: "الكمية", labelEn: "Qty", width: "10%", visible: true, order: 4 },
  { key: "price", labelAr: "السعر", labelEn: "Price", width: "10%", visible: true, order: 5 },
  { key: "total", labelAr: "المجموع", labelEn: "Total", width: "10%", visible: true, order: 6 },
  { key: "discount", labelAr: "الخصم", labelEn: "Discount", width: "10%", visible: true, order: 7 },
  { key: "tax", labelAr: "الضريبة", labelEn: "Tax", width: "10%", visible: true, order: 8 },
  { key: "net", labelAr: "الصافي", labelEn: "Net", width: "15%", visible: true, order: 9 },
  { key: "description", labelAr: "الوصف", labelEn: "Description", width: "100%", visible: false, order: 10 }, // New line description
];

const availableVariables = [
  { key: "{CompanyName}", label: "اسم الشركة" },
  { key: "{CompanyAddress}", label: "عنوان الشركة" },
  { key: "{CompanyPhone}", label: "هاتف الشركة" },
  { key: "{CompanyEmail}", label: "بريد الشركة" },
  { key: "{CompanyTax}", label: "الرقم الضريبي للشركة" },
  { key: "{InvoiceNumber}", label: "رقم الفاتورة" },
  { key: "{IssueDate}", label: "تاريخ الفاتورة" },
  { key: "{ReferenceNumber}", label: "الرقم المرجعي" },
  { key: "{CustomerName}", label: "اسم العميل" },
  { key: "{CustomerMobile}", label: "جوال العميل" },
  { key: "{CustomerAddress}", label: "عنوان العميل" },
  { key: "{CustomerTax}", label: "الرقم الضريبي للعميل" },
  { key: "{TotalBeforeTax}", label: "الإجمالي قبل الضريبة" },
  { key: "{TotalTax}", label: "إجمالي الضريبة" },
  { key: "{TotalDiscount}", label: "إجمالي الخصم" },
  { key: "{NetTotal}", label: "صافي الفاتورة" },
  { key: "{TotalText}", label: "المبلغ كتابة" },
  { key: "{QRCode}", label: "رمز QR" },
  { key: "{User}", label: "المستخدم" },
];

export default function PrintLayoutEditor() {
  const { t } = useTranslation();
  const companyId = parseInt(localStorage.getItem("companyId"), 10);
  
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [tabValue, setTabValue] = useState(0);
  
  const [layout, setLayout] = useState({
    headerHtml: "",
    footerHtml: "",
    customCss: "",
    showCompanyHeader: true,
    columns: defaultColumns,
    paperSize: "A4", // A4, A5, 80mm
  });

  const [draggedColIndex, setDraggedColIndex] = useState(null);

  useEffect(() => {
    if (companyId) {
      axios.get(`/InvoiceTypes?companyId=${companyId}`).then(res => {
        setInvoiceTypes(res.data);
        if (res.data.length > 0) setSelectedType(res.data[0].invoiceTypeId);
      });
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId && selectedType) {
      loadLayout();
    }
  }, [companyId, selectedType]);

  const loadLayout = async () => {
    try {
      const res = await printLayoutApi.get(companyId, selectedType);
      if (res.data && res.data.layoutJson) {
        const savedLayout = JSON.parse(res.data.layoutJson);
        // Merge with default columns to ensure new columns appear
        const mergedColumns = defaultColumns.map(defCol => {
          const savedCol = savedLayout.columns?.find(c => c.key === defCol.key);
          return savedCol ? { ...defCol, ...savedCol } : defCol;
        });
        // Sort by order
        mergedColumns.sort((a, b) => (a.order || 99) - (b.order || 99));
        setLayout({ ...savedLayout, columns: mergedColumns });
      } else {
        // Reset to defaults if no layout saved
        setLayout({
          headerHtml: "",
          footerHtml: "",
          customCss: "",
          showCompanyHeader: true,
          columns: defaultColumns,
          paperSize: "A4",
        });
      }
    } catch (err) {
      console.error("Failed to load layout", err);
    }
  };

  const handleSave = async () => {
    try {
      if (!selectedType) {
        toast.error("يرجى اختيار نوع الفاتورة أولاً");
        return;
      }
      await printLayoutApi.save({
        companyId,
        invoiceTypeId: selectedType,
        layoutJson: JSON.stringify(layout),
      });
      toast.success("تم حفظ تصميم الطباعة بنجاح");
    } catch (err) {
      console.error("Save error:", err);
      toast.error(err.response?.data?.message || "فشل حفظ التصميم");
    }
  };

  const handleColumnChange = (index, field, value) => {
    const newColumns = [...layout.columns];
    newColumns[index] = { ...newColumns[index], [field]: value };
    setLayout({ ...layout, columns: newColumns });
  };

  const handleColumnDragStart = (e, index) => {
    setDraggedColIndex(index);
    e.dataTransfer.effectAllowed = "move";
    // Set transparent drag image if needed, or default
  };

  const handleColumnDragOver = (e, index) => {
    e.preventDefault(); // Necessary to allow dropping
    e.dataTransfer.dropEffect = "move";
  };

  const handleColumnDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedColIndex === null || draggedColIndex === dropIndex) return;

    const newCols = [...layout.columns];
    const draggedItem = newCols[draggedColIndex];

    // Remove dragged item
    newCols.splice(draggedColIndex, 1);
    // Insert at new position
    newCols.splice(dropIndex, 0, draggedItem);

    // Re-assign order
    const reordered = newCols.map((col, idx) => ({ ...col, order: idx + 1 }));
    setLayout({ ...layout, columns: reordered });
    setDraggedColIndex(null);
  };

  const handleVariableDragStart = (e, variableKey) => {
    e.dataTransfer.setData("text/plain", variableKey);
    e.dataTransfer.effectAllowed = "copy";
  };

  const insertVariable = (variable) => {
    navigator.clipboard.writeText(variable);
    toast.info(`تم نسخ المتغير ${variable}، يمكنك لصقه في المحرر`);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }} dir="rtl">
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight="bold">🎨 محرر تصميم الطباعة</Typography>
          <Box display="flex" gap={2}>
            <Button variant="outlined" color="warning" onClick={() => loadLayout()} startIcon={<ResetIcon />}>
              إعادة تعيين
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave} startIcon={<SaveIcon />}>
              حفظ التصميم
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography mb={1}>نوع الفاتورة</Typography>
            <Select
              fullWidth
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>اختر نوع الفاتورة</MenuItem>
              {invoiceTypes.map(t => (
                <MenuItem key={t.invoiceTypeId} value={t.invoiceTypeId}>{t.name}</MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography mb={1}>حجم الورق</Typography>
            <Select
              fullWidth
              value={layout.paperSize}
              onChange={(e) => setLayout({ ...layout, paperSize: e.target.value })}
            >
              <MenuItem value="A4">A4 (قياسي)</MenuItem>
              <MenuItem value="A5">A5 (نصفي)</MenuItem>
              <MenuItem value="80mm">80mm (حراري/كاشير)</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} md={4} display="flex" alignItems="flex-end">
            <FormControlLabel
              control={
                <Checkbox
                  checked={layout.showCompanyHeader}
                  onChange={(e) => setLayout({ ...layout, showCompanyHeader: e.target.checked })}
                />
              }
              label="إظهار ترويسة الشركة الافتراضية (الشعار والبيانات)"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={3}>
          {/* Variables Sidebar */}
          <Grid item xs={12} md={3}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: '#f8f9fa' }}>
              <Typography variant="subtitle1" fontWeight="bold" mb={2} display="flex" alignItems="center">
                <CodeIcon sx={{ mr: 1 }} /> المتغيرات المتاحة
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                اضغط على المتغير لنسخه، ثم الصقه في المحرر
              </Typography>
              <List dense sx={{ maxHeight: 500, overflow: 'auto' }}>
                {availableVariables.map((v) => (
                  <ListItem
                    key={v.key}
                    button
                    draggable
                    onDragStart={(e) => handleVariableDragStart(e, v.key)}
                    onClick={() => insertVariable(v.key)}
                    sx={{ borderBottom: '1px solid #eee', bgcolor: 'white', mb: 0.5, borderRadius: 1, cursor: 'grab' }}
                  >
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CopyIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={v.label} secondary={v.key} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Main Editor Area */}
          <Grid item xs={12} md={9}>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
              <Tab icon={<TextFieldIcon />} label="الرأس (Header)" iconPosition="start" />
              <Tab icon={<ViewColumnIcon />} label="جدول الأصناف (Items)" iconPosition="start" />
              <Tab icon={<TextFieldIcon />} label="الذيل (Footer)" iconPosition="start" />
              <Tab icon={<CodeIcon />} label="CSS مخصص" iconPosition="start" />
            </Tabs>

            {/* Header Editor */}
            {tabValue === 0 && (
              <Box>
                <Typography mb={1}>محتوى إضافي في أعلى الفاتورة (HTML):</Typography>
                <Typography variant="caption" color="primary" mb={1} display="block">
                  💡 يمكنك سحب المتغيرات من القائمة الجانبية وإفلاتها هنا
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  value={layout.headerHtml}
                  onChange={(e) => setLayout({ ...layout, headerHtml: e.target.value })}
                  placeholder="<div>...</div>"
                  sx={{ direction: 'ltr', fontFamily: 'monospace' }}
                />
                <Typography variant="caption" color="textSecondary" mt={1} display="block">
                  يمكنك استخدام HTML و CSS هنا. استخدم المتغيرات مثل {"{CustomerName}"} لإدراج بيانات ديناميكية.
                </Typography>
              </Box>
            )}

            {/* Columns Editor */}
            {tabValue === 1 && (
              <Box>
                <Typography mb={2}>تخصيص أعمدة جدول الأصناف (اسحب لإعادة الترتيب):</Typography>
                {layout.columns.map((col, index) => (
                  <Paper 
                    key={col.key} 
                    variant="outlined" 
                    draggable
                    onDragStart={(e) => handleColumnDragStart(e, index)}
                    onDragOver={(e) => handleColumnDragOver(e, index)}
                    onDrop={(e) => handleColumnDrop(e, index)}
                    sx={{ 
                      p: 2, 
                      mb: 1, 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      bgcolor: draggedColIndex === index ? '#f0f0f0' : 'white',
                      opacity: draggedColIndex === index ? 0.5 : 1,
                      cursor: 'move',
                      transition: 'all 0.2s'
                    }}
                  >
                    {/* Drag Handle */}
                    <Box display="flex" alignItems="center" color="text.secondary">
                      <DragHandleIcon />
                    </Box>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={col.visible}
                          onChange={(e) => handleColumnChange(index, 'visible', e.target.checked)}
                        />
                      }
                      label=""
                    />
                    <Box width={100}>
                      <Typography variant="body2" fontWeight="bold">{col.key}</Typography>
                    </Box>
                    <TextField
                      label="العنوان (عربي)"
                      size="small"
                      value={col.labelAr}
                      onChange={(e) => handleColumnChange(index, 'labelAr', e.target.value)}
                    />
                    <TextField
                      label="العنوان (En)"
                      size="small"
                      value={col.labelEn}
                      onChange={(e) => handleColumnChange(index, 'labelEn', e.target.value)}
                    />
                    <TextField
                      label="العرض"
                      size="small"
                      value={col.width}
                      onChange={(e) => handleColumnChange(index, 'width', e.target.value)}
                      sx={{ width: 100 }}
                    />
                    <TextField
                      label="الترتيب"
                      type="number"
                      size="small"
                      value={col.order}
                      onChange={(e) => handleColumnChange(index, 'order', parseInt(e.target.value))}
                      sx={{ width: 80 }}
                    />
                  </Paper>
                ))}
              </Box>
            )}

            {/* Footer Editor */}
            {tabValue === 2 && (
              <Box>
                <Typography mb={1}>محتوى أسفل الفاتورة (HTML):</Typography>
                <Typography variant="caption" color="primary" mb={1} display="block">
                  💡 يمكنك سحب المتغيرات من القائمة الجانبية وإفلاتها هنا
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  value={layout.footerHtml}
                  onChange={(e) => setLayout({ ...layout, footerHtml: e.target.value })}
                  placeholder="<div>...</div>"
                  sx={{ direction: 'ltr', fontFamily: 'monospace' }}
                />
              </Box>
            )}

            {/* Custom CSS */}
            {tabValue === 3 && (
              <Box>
                <Typography mb={1}>CSS مخصص للفاتورة:</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  value={layout.customCss}
                  onChange={(e) => setLayout({ ...layout, customCss: e.target.value })}
                  placeholder=".invoice-table { border: 1px solid black; }"
                  sx={{ direction: 'ltr', fontFamily: 'monospace' }}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
}
